// src/pages/ProviderProfile.js
import React, { useEffect, useState, useCallback, useMemo, Suspense, lazy } from 'react';
import { useMap, MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import L from 'leaflet';
import { captureInstallPrompt, triggerInstallPrompt, initializeFirebaseMessaging } from '../serviceWorkerRegistration';
import { Helmet } from 'react-helmet-async';
import CustomStarRating from '../components/CustomStarRating';
import VideoPlayer from '../components/VideoPlayer';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Star, Play } from 'lucide-react';
import { toast } from "sonner";
import { Input } from '../components/ui/input';
import { Meteors } from '../components/ui/meteors';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Home.css';
import './ProviderProfile.css';
import 'leaflet/dist/leaflet.css';

// Lazy-load MapContainer to reduce initial bundle size
const LazyMapContainer = lazy(() => import('react-leaflet').then(module => ({ default: module.MapContainer })));

const API_URL = process.env.REACT_APP_API_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const providerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const fetchProvider = async (providerId) => {
  const response = await axios.get(
    `${API_URL}/api/provider/${providerId}`,
    { withCredentials: true }
  );
  return response.data;
};

const fetchUser = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/profile/`, {
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    return null; // Return null for anonymous users
  }
};

const fetchRatingsSummary = async (providerId) => {
  const response = await axios.get(
    `${API_URL}/api/provider/${providerId}/ratings_summary/`,
    { withCredentials: true }
  );
  return response.data;
};

const fetchCanChat = async (providerId) => {
  const response = await axios.get(
    `${API_URL}/api/can_chat/${providerId}/`,
    { withCredentials: true }
  );
  return response.data;
};

const fetchProviderImages = async (providerId) => {
  const response = await axios.get(`${API_URL}/api/provider/${providerId}/images/`, { withCredentials: true });
  return response.data;
};

const fetchProviderVideos = async (id) => {
  const response = await axios.get(`${API_URL}/api/provider/${id}/videos/`, { withCredentials: true });
  return response.data;
};

// Optimized geocodeLocation with static Nairobi coords for anonymous users
const geocodeLocation = async (location, queryClient) => {
  if (
    !location ||
    typeof location !== 'string' ||
    location.trim() === '' ||
    location === 'Not set'
  ) {
    console.warn('Invalid or empty location:', location);
    return null;
  }

  // Skip geocoding for Nairobi (common for anonymous users)
  if (location.trim().toLowerCase() === 'nairobi') {
    return { lat: -1.2921, lng: 36.8219 };
  }

  const queryKey = ['geocode', location.trim()];
  const cached = queryClient.getQueryData(queryKey);
  if (cached) {
    return cached;
  }

  const query = `${location.trim()}, Kenya`;
  try {
    await new Promise((resolve) => setTimeout(resolve, 100)); // Debounce
    const response = await axios.get(
      `https://nominatim.openstreetmap.org/search`,
      {
        params: {
          q: query,
          format: 'json',
          limit: 1,
        },
        headers: {
          'User-Agent': 'SErraND/1.0 (contact: admin@serrand.org)',
        },
        withCredentials: false,
      }
    );
    const data = response.data[0];
    if (data) {
      const coords = { lat: parseFloat(data.lat), lng: parseFloat(data.lon) };
      queryClient.setQueryData(queryKey, coords, { staleTime: 24 * 60 * 60 * 1000 });
      return coords;
    }
    toast.error(`Could not geocode location: "${location}". Using default (Nairobi).`);
    return null;
  } catch (error) {
    console.error('Geocoding failed:', error.message);
    toast.error(`Geocoding failed for "${location}". Using default (Nairobi).`);
    return null;
  }
};

const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c).toFixed(2);
};

const MapViewController = ({ userCoords, providerCoords }) => {
  const map = useMap();

  useEffect(() => {
    if (userCoords && providerCoords) {
      const bounds = L.latLngBounds(
        [userCoords.lat, userCoords.lng],
        [providerCoords.lat, providerCoords.lng]
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else if (providerCoords) {
      map.setView([providerCoords.lat, providerCoords.lng], 13);
    }
  }, [map, userCoords, providerCoords]);

  return null;
};

function ProviderProfile() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [chatInitiated, setChatInitiated] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [userCoords, setUserCoords] = useState(null);
  const [providerCoords, setProviderCoords] = useState(null);
  const [distance, setDistance] = useState(null);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const [showScoringInfo, setShowScoringInfo] = useState(false);
  const [showReviewsPanel, setShowReviewsPanel] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [expectedAmount, setExpectedAmount] = useState('');

  const { darkMode, toggleDarkMode } = useTheme();
  const { user, openLogin, userLoading } = useAuth();

  const {
    data: provider,
    isLoading: providerLoading,
    error: providerError,
  } = useQuery({
    queryKey: ['provider', providerId],
    queryFn: () => fetchProvider(providerId),
    onError: (err) => {
      console.error('Provider fetch error:', err);
      toast.error(
        err.response?.data?.error || 'Failed to load provider profile.'
      );
    },
  });

  const { data: ratingsSummary, isLoading: ratingsLoading } = useQuery({
    queryKey: ['ratingsSummary', providerId],
    queryFn: () => fetchRatingsSummary(providerId),
    enabled: !!provider,
    staleTime: 10 * 60 * 1000,
  });

  const { data: canChatData, isLoading: canChatLoading } = useQuery({
    queryKey: ['canChat', providerId],
    queryFn: () => fetchCanChat(providerId),
    enabled: !!provider && !!user,
    onError: (err) => {
      console.error('CanChat fetch error:', err);
      toast.error(
        err.response?.data?.error || 'Failed to check chat eligibility.'
      );
    },
  });

  const { data: providerImages, isLoading: imagesLoading } = useQuery({
    queryKey: ['providerImages', providerId],
    queryFn: () => fetchProviderImages(providerId),
    enabled: !!provider,
    staleTime: 10 * 60 * 1000,
  });

  const { data: providerVideos = [], isLoading: videosLoading } = useQuery({
    queryKey: ['providerVideos', providerId],
    queryFn: () => fetchProviderVideos(providerId),
    enabled: !!provider,
    staleTime: 10 * 60 * 1000,
  });

  const fetchCoordinates = async () => {
    const defaultCoords = { lat: -1.2921, lng: 36.8219 }; // Nairobi

    if (user?.latitude && user?.longitude) {
      setUserCoords({ lat: user.latitude, lng: user.longitude });
    } else {
      const userLocation = user?.location || 'Nairobi';
      if (userLocation.toLowerCase() === 'nairobi') {
        setUserCoords(defaultCoords);
      } else {
        const coords = await geocodeLocation(userLocation, queryClient);
        setUserCoords(coords || defaultCoords);
      }
    }

    if (provider?.latitude && provider?.longitude) {
      setProviderCoords({ lat: provider.latitude, lng: provider.longitude });
      setGeocodeFailed(false);
    } else {
      const providerLocation = provider?.location || 'Nairobi';
      if (providerLocation === 'Not set' || providerLocation.toLowerCase() === 'nairobi') {
        setProviderCoords(defaultCoords);
        setGeocodeFailed(providerLocation !== 'Not set');
      } else {
        const coords = await geocodeLocation(providerLocation, queryClient);
        setProviderCoords(coords || defaultCoords);
        setGeocodeFailed(!coords);
      }
    }
  };

  useEffect(() => {
    if (provider) {
      fetchCoordinates();
    }
  }, [provider, user]);


  useEffect(() => {
    const { state } = location;

    if (state?.restoreBooking) {
      const booking = state.restoreBooking;

      setDescription(booking.description || '');
      setScheduledAt(booking.scheduledAt || '');
      if (booking.expectedAmount) setExpectedAmount(booking.expectedAmount);

      setShowOrderForm(true);

      toast.info("Your previous order details have been restored.", {
        description: "Please review before submitting.",
      });
    }
  }, [location]);

  // Handle install prompt
  useEffect(() => {
    captureInstallPrompt((prompt) => {
      setCanInstall(true);
    });
  }, []);

  useEffect(() => {
    const locationState = location.state;

    if (locationState?.restoreBooking) {
      const { description, scheduledAt, expectedAmount } = locationState.restoreBooking;

      setDescription(description || '');
      setScheduledAt(scheduledAt || '');
      setExpectedAmount(expectedAmount || '');

      // Auto-open the order form
      setShowOrderForm(true);

      // Clear the state so it doesn't trigger again
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('order') === 'true') {
      setShowOrderForm(true);
      // Clear query param to prevent re-triggering on refresh
      navigate(`/provider/${providerId}`, { replace: true });
    }
  }, [location, navigate, providerId]);

  const calculatedDistance = useMemo(() => {
    if (userCoords && providerCoords) {
      return calculateDistance(
        userCoords.lat,
        userCoords.lng,
        providerCoords.lat,
        providerCoords.lng
      );
    }
    return null;
  }, [userCoords, providerCoords]);

  useEffect(() => {
    setDistance(calculatedDistance);
  }, [calculatedDistance]);

  const handleChatClick = () => {
    if (!user) {
      toast.error(
        'Please log in to start a chat. Open the hamburger menu (on mobile) or use the navbar (on desktop) to log in or register.'
      );
      navigate('/', { state: { showLogin: true } });
      return;
    }
    if (!canChatData?.can_chat) {
      toast.error(
        canChatData?.error || 'Chat is only available during an active order.'
      );
      return;
    }
    setChatInitiated(true);
    navigate(`/profile?chat_with=${provider.user.id}`);
    toast.success('Opening chat.');
  };

  const handleOrderService = async () => {
    if (!description || !scheduledAt) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!user) {
      // Save pending booking
      const pendingBooking = {
        providerId,
        description,
        scheduledAt,
        expectedAmount: expectedAmount || null,
        timestamp: Date.now()
      };
      localStorage.setItem('pendingBooking', JSON.stringify(pendingBooking));

      openLogin({
        title: "Almost There!",
        subtitle: "Log in or Register to confirm your booking. We'll save your order details."
      });

      return;
    }

    // User is already logged in → submit normally
    setIsSubmittingOrder(true);
    try {
      await axios.post(
        `${API_URL}/api/order_service/${providerId}/`,
        {
          description,
          scheduled_at: scheduledAt,
          estimated_amount: expectedAmount || null,
        },
        { withCredentials: true }
      );

      toast.success("Service request submitted successfully!");
      localStorage.removeItem('pendingBooking');
      setShowOrderForm(false);
      setDescription('');
      setScheduledAt('');
      setExpectedAmount('');
      navigate('/manage-orders');
    } catch (err) {
      console.error('Order failed:', err.response?.data || err.message);
      toast.error(err.response?.data?.error || 'Failed to submit order. Please try again.');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      toast.success('Logged out successfully.');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const getMinCharge = useCallback((providerData) => {
    const services = providerData?.services || [];
    if (services.length === 0) return 300;

    let highestCharge = 0;

    for (const service of services) {
      // Custom charge — only for General Services
      if (
        service.is_general_category &&
        service.custom_minimum_charge &&
        service.custom_minimum_charge > 0
      ) {
        highestCharge = Math.max(highestCharge, service.custom_minimum_charge);
      }
      // Category default charge (Plumbing = 1500, etc.)
      else if (service.category_default_min_charge) {
        highestCharge = Math.max(highestCharge, service.category_default_min_charge);
      }
    }

    return highestCharge > 0 ? highestCharge : 300;
  }, []);

  // Image navigation handlers
  const handleNextImage = () => {
    if (providerImages && selectedImageIndex !== null) {
      setSelectedImageIndex((prev) => (prev + 1) % providerImages.length);
    }
  };

  const handlePrevImage = () => {
    if (providerImages && selectedImageIndex !== null) {
      setSelectedImageIndex((prev) => (prev - 1 + providerImages.length) % providerImages.length);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (selectedImageIndex !== null) {
        if (e.key === 'ArrowRight') handleNextImage();
        if (e.key === 'ArrowLeft') handlePrevImage();
        if (e.key === 'Escape') setSelectedImageIndex(null);
      }
      if (selectedVideo !== null && e.key === 'Escape') {
        setSelectedVideo(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedImageIndex, selectedVideo, providerImages]);

  const trustColor = useMemo(
    () =>
      ['A', 'B'].includes(provider?.trust_tier)
        ? 'text-green-400'
        : provider?.trust_tier === 'E'
        ? 'text-red-400'
        : 'text-yellow-400',
    [provider]
  );
  const isTopTier = useMemo(
    () => ['A', 'B'].includes(provider?.trust_tier),
    [provider]
  );
  const recentReview = provider?.recent_review;
  const allReviews = provider?.all_reviews || [];

  const pageTitle = provider
    ? `${provider.business_name} - Service Provider Profile | SErraND`
    : 'Provider Profile | SErraND';
  const pageDescription = provider
    ? `${
        provider.description?.slice(0, 160) ||
        `Hire ${provider.business_name} for ${
          provider.service_provided || 'reliable services'
        } in ${provider.location || 'Kenya'}.`
      }...`
    : 'Connect with trusted service providers in Kenya on SErraND. Find reliable professionals for all your needs.';
  const pageKeywords = provider
    ? `${
        provider.service_provided || 'service provider'
      }, ${provider.business_name}, ${provider.location || 'Kenya'}, SErraND, local services, hire professional`
    : 'SErraND, service providers, Kenya, local services, kazi, mtu wa kazi, pros, services near me, hire professionals, trusted gurus';
  const canonicalUrl = `https://www.serrand.org/provider/${providerId}`;
  const ogImage =
    provider?.user?.profile_picture ||
    'https://www.serrand.org/static/images/og-image.jpg';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': provider?.is_individual ? 'Person' : 'Organization',
      name: provider?.business_name || 'Service Provider',
      description:
        provider?.description ||
        `Provider of ${provider?.service_provided || 'services'} in ${
          provider?.location || 'Kenya'
        }.`,
      url: canonicalUrl,
      address: {
        '@type': 'PostalAddress',
        addressLocality: provider?.location || 'Nairobi',
        addressCountry: 'KE',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        email: provider?.email || 'plugwakazisupport@serrand.org',
      },
      image: ogImage,
    },
    publisher: {
      '@type': 'Organization',
      name: 'SErraND',
      url: 'https://www.serrand.org',
      logo: 'https://www.serrand.org/images/logo.png',
    },
  };

  if (providerLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-xl">Loading provider details...</p>
        </div>
      </div>
    );
  }
  if (!provider || providerError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-xl p-8 max-w-md mx-auto">
            <h3 className="text-xl font-bold text-red-400 mb-2">Provider Not Found</h3>
            <p className="text-red-300 mb-4">The requested provider profile could not be loaded.</p>
            <Link 
              to="/" 
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all inline-block"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Marvin Useki" />
        <meta name="viewport" content="width=device-width, initial-scale=0.9, maximum-scale=1.0, user-scalable=yes" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="SErraND" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:site" content="@SErraND" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Meteors count={40} angle={220} />

      <Navbar
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        openLogin={() => navigate('/', { state: { showLogin: true } })}
        openRegister={() => navigate('/', { state: { showRegister: true } })}
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
      />

      
{/* Hero Section */}
    <section className="relative py-12 px-4 text-center bg-background">
      <div className="max-w-4xl mx-auto relative z-10">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
          {provider.business_name}
        </h1>

        {isTopTier && (
          <div className="inline-flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-4 py-1 rounded-full text-sm font-medium mb-4">
            ⭐ Top-Rated Guru
          </div>
        )}

        <p className="text-xl text-muted-foreground mb-2">
          {provider.service_provided || 'Professional Service Provider'}
        </p>

        <p className="text-base text-muted-foreground">
          📍 {provider.location || 'Kenya'} • Trust Score: 
          <span className="font-medium text-foreground ml-1">
            {provider.trust_score?.toFixed(1) || '0.0'}
          </span>
        </p>
      </div>
    </section>      

      {/* Main Provider Profile Section */}
<section className="py-12 px-4 max-w-7xl mx-auto relative z-10">
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

    {/* Left Column - Provider Details */}
    <div className="lg:col-span-8">
      <Card className="p-8">
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {provider.business_name}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground">Username:</span>{' '}
                  <span className="font-medium text-foreground">{provider.user?.username}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Service:</span>{' '}
                  <span className="font-medium text-foreground">
                    {provider.service_provided || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Location:</span>{' '}
                  <span className="font-medium text-foreground">
                    {provider.location || 'Not set'}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground">Hours:</span>{' '}
                  <span className="font-medium text-foreground">
                    {provider.business_hours || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Trust Score:</span>{' '}
                  <span className={trustColor}>
                    {provider.trust_score?.toFixed(1) || '0.0'} 
                    (Tier {provider.trust_tier || 'E'})
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-4">
              <CustomStarRating
                count={5}
                value={provider.average_rating || 0}
                size={20}
                readOnly
              />
              <span className="text-muted-foreground">
                {provider.average_rating?.toFixed(1) || '0.0'}/5 
                ({provider.rating_count || 0} reviews)
              </span>
              {distance && (
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                  📍 {distance} km away
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {provider.description && (
          <Card className="p-6 mb-8 bg-muted/50">
            <h3 className="text-xl font-semibold mb-3 text-foreground">About This Service</h3>
            <p className="text-muted-foreground leading-relaxed">
              {provider.description}
            </p>
          </Card>
        )}

        {/* Recent Review */}
        {recentReview ? (
          <Card className="p-6 mb-8 border-l-4 border-primary">
            <h3 className="text-xl font-semibold mb-4">Most Recent Review</h3>
            <div className="flex items-center gap-3 mb-3">
              <CustomStarRating count={5} value={recentReview.rating} size={18} readOnly />
              <span className="text-sm text-muted-foreground">
                by {recentReview.display_name}
              </span>
            </div>
            <p className="italic text-muted-foreground">
              "{recentReview.review || 'Great service!'}"
            </p>
          </Card>
        ) : (
          <Card className="p-6 mb-8 text-center bg-muted/30">
            <p className="text-muted-foreground">No reviews yet — be the first to hire this guru! 🌟</p>
          </Card>
        )}

{/* Action Buttons */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
  <Button 
    onClick={() => setShowOrderForm(true)} 
    className="h-14 text-base font-medium"
  >
    🛒 Order Service
  </Button>

  <Button 
    onClick={() => setShowScoringInfo(true)} 
    variant="outline" 
    className="h-14 text-base font-medium"
  >
    ℹ️ How Scoring Works
  </Button>

  {provider.rating_count > 0 && (
    <Button 
      onClick={() => setShowReviewsPanel(true)} 
      variant="outline" 
      className="h-14 text-base font-medium"
    >
      View All Reviews ({provider.rating_count})
    </Button>
  )}
</div>

        {/* Media Section: Videos + Images */}
        <div className="space-y-12">
          {/* VIDEOS */}
          {videosLoading ? (
            <div className="text-center py-8 text-gray-400">Loading videos...</div>
          ) : Array.isArray(providerVideos) && providerVideos.length > 0 ? (
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                Service Videos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {providerVideos.map((video) => (
                  <div
                    key={video.id}
                    className="relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer group bg-black"
                    onClick={() => setSelectedVideo(video)}
                  >
                    <VideoPlayer url={video.video} className="w-full h-64 object-cover rounded-2xl" />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white text-black rounded-full w-16 h-16 flex items-center justify-center shadow-2xl">
                        <span className="text-3xl ml-1">Play</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

{/* YouTube Video Modal */}
{selectedVideo && (
  <div
    className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4"
    onClick={() => setSelectedVideo(null)}
  >
    <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setSelectedVideo(null)}
        className="absolute -top-12 right-4 text-white text-5xl hover:text-gray-300 transition-colors z-10"
      >
        ×
      </button>

      <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${selectedVideo.video_id || selectedVideo.video}?autoplay=1&modestbranding=1&rel=0&playsinline=1`}
          title={selectedVideo.title || "Service Video"}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>

      <div className="text-center mt-4 text-white text-lg font-medium">
        {selectedVideo.title || "Service Video"}
      </div>
    </div>
  </div>
)}

          {/* Images Gallery */}
          <div>
            <h3 className="text-2xl font-semibold mb-6 text-foreground">
              Gallery {providerImages?.length > 0 && `(${providerImages.length})`}
            </h3>

            {Array.isArray(providerImages) && providerImages.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {providerImages.map((img, index) => (
                  <div
                    key={img.id}
                    onClick={() => setSelectedImageIndex(index)}
                    className="aspect-square rounded-xl overflow-hidden border border-border cursor-pointer hover:scale-105 transition-transform duration-300"
                  >
                    <img
                      src={img.image}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">No photos uploaded yet</p>
              </Card>
            )}
          </div>
        </div>
      </Card>
    </div>

    {/* Right Column - Location Map */}
    <div className="lg:col-span-4">
      <Card className="sticky top-6 p-6">
        <h3 className="text-2xl font-semibold mb-6 text-foreground">📍 Location</h3>
        
        {userCoords && providerCoords ? (
          <div className="h-80 rounded-xl overflow-hidden border border-border">
            {/* Your existing map code goes here */}
            <LazyMapContainer
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <MapViewController
                userCoords={userCoords}
                providerCoords={providerCoords}
              />
              <Marker position={[userCoords.lat, userCoords.lng]} icon={userIcon}>
                <Popup>
                  <div className="text-center p-2">
                    <strong>Your Location</strong>
                    <br />
                    {user?.location || 'Default: Nairobi'}
                  </div>
                </Popup>
              </Marker>
              <Marker position={[providerCoords.lat, providerCoords.lng]} icon={providerIcon}>
                <Popup>
                  <div className="text-center p-2">
                    <strong>{provider.business_name}</strong>
                    <br />
                    {provider.location || 'Default: Nairobi'}
                  </div>
                </Popup>
              </Marker>
            </LazyMapContainer>
          </div>
        ) : (
          <div className="h-80 bg-muted rounded-xl flex items-center justify-center">
            <p className="text-muted-foreground">Loading map...</p>
          </div>
        )}

        {distance && (
          <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl text-center">
            <p className="text-emerald-700 dark:text-emerald-400 font-medium">
              📍 {distance} km away
            </p>
          </div>
        )}
      </Card>
    </div>
  </div>
</section>

{/* Order Form Modal */}
{showOrderForm && (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <h3 className="text-2xl font-semibold text-foreground">Order Service</h3>
        <button
          onClick={() => {
            setShowOrderForm(false);
            setDescription('');
            setScheduledAt('');
            setExpectedAmount('');
          }}
          className="text-2xl text-muted-foreground hover:text-foreground transition-colors"
        >
          ×
        </button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            What needs to be done?
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Fix leaking pipe in kitchen + replace washer"
            className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary resize-y"
            required
          />
        </div>

        {/* Schedule */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            When should the service be done?
          </label>
          <Input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
            className="text-base"
            required
          />
        </div>

        {/* Expected Budget */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            What's your approximate budget? (optional)
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">KSh</span>
            <Input
              type="number"
              value={expectedAmount}
              onChange={(e) => setExpectedAmount(e.target.value)}
              placeholder="5000"
              className="pl-16 text-base"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Minimum: KSh {getMinCharge(provider)} • No upper limit
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleOrderService}
            disabled={!description || !scheduledAt || isSubmittingOrder}
            className="flex-1 h-12 text-base"
          >
            {isSubmittingOrder ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Submitting...
              </div>
            ) : (
              'Submit & Pay After Job'
            )}
          </Button>

          <Button
            variant="outline"
            onClick={() => setShowOrderForm(false)}
            className="h-12"
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
)}

{/* Scoring Info Modal */}
{showScoringInfo && (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <h2 className="text-2xl font-semibold text-foreground">How Our Scoring Works ⭐</h2>
        <button
          onClick={() => setShowScoringInfo(false)}
          className="text-2xl text-muted-foreground hover:text-foreground"
        >
          ×
        </button>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="p-5 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border border-emerald-200 dark:border-emerald-800">
          <h3 className="font-semibold text-emerald-700 dark:text-emerald-400 mb-2">Quality Ratings</h3>
          <p className="text-muted-foreground">After completing an order, clients rate providers (1-5 stars) with optional reviews. These ratings directly impact the provider's trust score.</p>
        </div>

        <div className="p-5 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Provider Trust Score (0-100)</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li><strong>Quality Ratings (40%)</strong> — Average of all client ratings</li>
            <li><strong>Transaction Honesty (20%)</strong> — Lower cash payment usage increases score</li>
            <li><strong>Service Smoothness (20%)</strong> — Based on acceptance, completion, and on-time rates</li>
            <li><strong>M-Pesa Preference (20%)</strong> — Using M-Pesa boosts the score</li>
          </ul>
        </div>

        <div className="p-5 bg-purple-50 dark:bg-purple-950/30 rounded-xl border border-purple-200 dark:border-purple-800">
          <h3 className="font-semibold text-purple-700 dark:text-purple-400 mb-2">Trust Tiers</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong className="text-red-500">E (≤30)</strong> — New/Low</div>
            <div><strong className="text-orange-500">D (≤45)</strong> — Below Average</div>
            <div><strong className="text-yellow-500">C (≤60)</strong> — Average</div>
            <div><strong className="text-green-500">B (≤80)</strong> — Good</div>
            <div><strong className="text-emerald-500">A (&gt;80)</strong> — Excellent</div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">Higher tiers improve visibility in search results.</p>
        </div>

        <div className="p-5 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
          <h3 className="font-semibold text-amber-700 dark:text-amber-400 mb-2">User Reputation Score</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Base score: 50 points</li>
            <li><strong>Completions:</strong> +30% per completed order</li>
            <li><strong>Cancellations:</strong> -20% per cancellation</li>
          </ul>
        </div>
      </CardContent>

      <div className="p-6 border-t">
        <Button onClick={() => setShowScoringInfo(false)} className="w-full">
          Got It! 👍
        </Button>
      </div>
    </Card>
  </div>
)}

{/* Reviews Panel Modal */}
{showReviewsPanel && (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <h2 className="text-2xl font-semibold text-foreground">
          All Reviews for <span className="text-primary">{provider.business_name}</span>
        </h2>
        <button
          onClick={() => setShowReviewsPanel(false)}
          className="text-2xl text-muted-foreground hover:text-foreground"
        >
          ×
        </button>
      </CardHeader>

      <CardContent className="space-y-6">
        {allReviews.length > 0 ? (
          allReviews.map((review) => (
            <Card key={review.id} className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <CustomStarRating count={5} value={review.rating} size={18} readOnly />
                <span className="text-sm text-muted-foreground">
                  by {review.display_name}
                </span>
              </div>
              <p className="italic text-foreground">
                "{review.review || 'No review text provided.'}"
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                {new Date(review.created_at).toLocaleDateString()}
              </p>
            </Card>
          ))
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No reviews available yet.</p>
          </div>
        )}
      </CardContent>

      <div className="p-6 border-t">
        <Button onClick={() => setShowReviewsPanel(false)} className="w-full">
          Close Reviews
        </Button>
      </div>
    </Card>
  </div>
)}

{/* Image Gallery Modal */}
{selectedImageIndex !== null && Array.isArray(providerImages) && providerImages[selectedImageIndex] && (
  <div
    className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    onClick={() => setSelectedImageIndex(null)}
  >
    <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setSelectedImageIndex(null)}
        className="absolute -top-12 right-4 text-white text-5xl hover:text-gray-300 transition"
      >
        ×
      </button>

      {Array.isArray(providerImages) && providerImages.length > 1 && (
        <>
          <button
            onClick={handlePrevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl bg-black/60 hover:bg-black/80 px-4 py-2 rounded-full transition"
          >
            ←
          </button>
          <button
            onClick={handleNextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl bg-black/60 hover:bg-black/80 px-4 py-2 rounded-full transition"
          >
            →
          </button>
        </>
      )}

      <img
        src={providerImages[selectedImageIndex].image}
        alt={`${provider.business_name} Service ${selectedImageIndex + 1}`}
        className="w-full h-auto max-h-[85vh] object-contain rounded-2xl shadow-2xl"
      />

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white px-5 py-1.5 rounded-full text-sm">
        {selectedImageIndex + 1} of {providerImages.length}
      </div>
    </div>
  </div>
)}

{/* VIDEO FULLSCREEN MODAL - YouTube Optimized */}
 {/* VIDEO FULLSCREEN MODAL */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute top-4 right-4 text-white text-5xl bg-black bg-opacity-70 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-90 transition z-10"
            >
              ×
            </button>
            <VideoPlayer url={selectedVideo.video} className="w-full rounded-2xl shadow-2xl" />
          </div>
        </div>
      )}


      {/* Enhanced Footer */}
       <Footer
        canInstall={canInstall}
        triggerInstallPrompt={triggerInstallPrompt}
      />
    </div>
  );
}

export default ProviderProfile;
